"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building, User, Mail, MapPin, FileText } from "lucide-react";
import type { QuickBooksInvoice } from "@/lib/types/quickbooks-unified";

interface InvoiceCustomerTabProps {
  invoice?: QuickBooksInvoice;
}

/**
 * Customer tab component for invoice details
 * Displays customer information including name, email, address, and memo
 */
export const InvoiceCustomerTab: React.FC<InvoiceCustomerTabProps> = ({ invoice }) => {
  if (!invoice?.CustomerRef) {
    return (
      <Card>
        <CardContent className="text-center py-10">
          <Building className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600">
            No customer information available for this invoice.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Building className="h-4 w-4" />
          Customer Information
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-6">
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-1">
                <User className="h-3.5 w-3.5" />
                Customer Name
              </div>
              <div className="text-lg font-semibold">{invoice.CustomerRef.name || "N/A"}</div>
              <div className="text-xs text-gray-500 mt-1">ID: {invoice.CustomerRef.value}</div>
            </div>

            {invoice.BillEmail?.Address && (
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-1">
                  <Mail className="h-3.5 w-3.5" />
                  Email Address
                </div>
                <div className="text-base">{invoice.BillEmail.Address}</div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {invoice.BillAddr?.Line1 && (
              <div className="p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-1">
                  <MapPin className="h-3.5 w-3.5" />
                  Billing Address
                </div>
                <div className="text-base">{invoice.BillAddr.Line1}</div>
              </div>
            )}

            {invoice.CustomerMemo?.value && (
              <div className="p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-1">
                  <FileText className="h-3.5 w-3.5" />
                  Customer Memo
                </div>
                <div className="text-sm italic leading-relaxed">
                  &ldquo;{invoice.CustomerMemo.value}&rdquo;
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
